import { useState, useEffect, useCallback } from "react";
import { listTeam, inviteTeamMember, removeTeamMember } from "../lib/api";
import Sidebar from "../components/Sidebar";

const BLUE   = "#0176CE";
const ORANGE = "#F37428";

function formatDate(raw) {
  if (!raw) return "—";
  return new Date(raw).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function RoleBadge({ role }) {
  const cfg = {
    client: { color: BLUE,     bg: `rgba(1,118,206,0.1)`   },
    admin:  { color: "#7c3aed", bg: "rgba(124,58,237,0.1)"  },
    member: { color: "#059669", bg: "rgba(5,150,105,0.08)"  },
    viewer: { color: "#64748b", bg: "rgba(100,116,139,0.1)" },
  };
  const c = cfg[role] || cfg.member;
  return (
    <span style={{ background: c.bg, color: c.color, border: `1px solid ${c.color}33`,
      borderRadius: 6, fontSize: 11, fontWeight: 600, padding: "3px 9px",
      textTransform: "capitalize", letterSpacing: "0.04em" }}>
      {role}
    </span>
  );
}

function InviteModal({ onClose, onSuccess }) {
  const [email, setEmail]     = useState("");
  const [role, setRole]       = useState("member");
  const [sending, setSending] = useState(false);
  const [err, setErr]         = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setSending(true); setErr("");
    try {
      await inviteTeamMember(email.trim(), role);
      onSuccess(email.trim());
    } catch (e) {
      setErr(e.message || "Failed to send invitation.");
      setSending(false);
    }
  }

  return (
    <div style={modalStyles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyles.dialog}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>Add team member</h2>
          <button style={modalStyles.closeBtn} onClick={onClose} disabled={sending}>✕</button>
        </div>
        <p style={modalStyles.sub}>They'll receive an email with a link to create their account and join your team.</p>
        <form onSubmit={handleSubmit}>
          <div style={modalStyles.field}>
            <label style={modalStyles.label}>Email address</label>
            <input style={modalStyles.input} type="email" placeholder="colleague@company.com"
              value={email} onChange={e => setEmail(e.target.value)} required disabled={sending} autoFocus />
          </div>
          <div style={modalStyles.field}>
            <label style={modalStyles.label}>Role</label>
            <select style={modalStyles.select} value={role} onChange={e => setRole(e.target.value)} disabled={sending}>
              <option value="member">Member — can upload and view jobs</option>
              <option value="admin">Admin — can manage team and invite others</option>
              <option value="viewer">Viewer — read-only access</option>
            </select>
          </div>
          {err && <p style={{ color: "#ef4444", fontSize: 13, margin: "0 0 14px" }}>{err}</p>}
          <div style={modalStyles.actions}>
            <button type="button" style={modalStyles.cancelBtn} onClick={onClose} disabled={sending}>Cancel</button>
            <button type="submit" style={modalStyles.sendBtn} disabled={sending || !email.trim()}>
              {sending ? "Sending…" : "Send invitation →"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Team() {
  const [members, setMembers]       = useState([]);
  const [pending, setPending]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [showModal, setShowModal]   = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [removing, setRemoving]     = useState(null);

  const canManage = ["client", "admin"].includes(
    JSON.parse(localStorage.getItem("mfai_user") || "null")?.role
  );

  const fetchTeam = useCallback(async () => {
    try {
      const res = await listTeam();
      setMembers(res.members || []);
      setPending(res.pending_invites || []);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load team.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  function handleInviteSuccess(email) {
    setShowModal(false);
    setSuccessMsg(`Invitation sent to ${email}`);
    fetchTeam();
    setTimeout(() => setSuccessMsg(""), 5000);
  }

  async function handleRemove(userId, name) {
    if (!window.confirm(`Remove ${name} from your team?`)) return;
    setRemoving(userId);
    try { await removeTeamMember(userId); fetchTeam(); }
    catch (e) { alert(e.message || "Failed to remove member."); }
    finally { setRemoving(null); }
  }

  const user = JSON.parse(localStorage.getItem("mfai_user") || "null");

  return (
    <div style={styles.page}>
      {showModal && <InviteModal onClose={() => setShowModal(false)} onSuccess={handleInviteSuccess} />}

      <div style={styles.topStripe} />
      <div style={styles.body}>
        <Sidebar active="team" />

        <main style={styles.main}>
          <div style={styles.header}>
            <div>
              <h1 style={styles.pageTitle}>Team</h1>
              <p style={styles.pageSub}>Manage your team members and invitations</p>
            </div>
            {canManage && (
              <button style={styles.addBtn} onClick={() => setShowModal(true)}>+ Add member</button>
            )}
          </div>

          {successMsg && <div style={styles.successBanner}>✓ {successMsg}</div>}

          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Members</h2>
            {loading && <div style={{ padding: "32px 0", textAlign: "center" }}><div style={styles.spinner} /></div>}
            {error && <p style={{ color: "#ef4444", fontSize: 13, padding: "16px 0" }}>{error}</p>}
            {!loading && !error && (
              <table style={styles.table}>
                <thead>
                  <tr>
                    {["Name", "Email", "Role", "Joined", canManage ? "Actions" : ""].filter(Boolean).map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m.user_id} style={styles.tr}>
                      <td style={styles.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ ...styles.avatar, width: 28, height: 28, fontSize: 11 }}>
                            {m.full_name?.[0]?.toUpperCase() || "?"}
                          </div>
                          <span style={{ fontWeight: 500, color: "#0f172a" }}>
                            {m.full_name || "—"}
                            {m.user_id === user?.user_id && <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: 12, marginLeft: 6 }}>(you)</span>}
                          </span>
                        </div>
                      </td>
                      <td style={{ ...styles.td, color: "#64748b" }}>{m.email}</td>
                      <td style={styles.td}><RoleBadge role={m.role} /></td>
                      <td style={{ ...styles.td, color: "#94a3b8", fontSize: 12 }}>{formatDate(m.created_on_utc)}</td>
                      {canManage && (
                        <td style={styles.td}>
                          {m.role !== "client" && m.user_id !== user?.user_id && (
                            <button style={styles.removeBtn} onClick={() => handleRemove(m.user_id, m.full_name)} disabled={removing === m.user_id}>
                              {removing === m.user_id ? "Removing…" : "Remove"}
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {pending.length > 0 && (
            <div style={{ ...styles.card, marginTop: 24 }}>
              <h2 style={styles.cardTitle}>Pending invitations</h2>
              <p style={styles.cardSub}>Waiting for recipients to accept</p>
              <table style={styles.table}>
                <thead>
                  <tr>{["Email", "Role", "Sent", "Expires"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {pending.map(inv => (
                    <tr key={inv.invite_id} style={styles.tr}>
                      <td style={styles.td}>{inv.email}</td>
                      <td style={styles.td}><RoleBadge role={inv.role} /></td>
                      <td style={{ ...styles.td, color: "#94a3b8", fontSize: 12 }}>{formatDate(inv.created_on_utc)}</td>
                      <td style={{ ...styles.td, color: "#94a3b8", fontSize: 12 }}>{formatDate(inv.expires_on_utc)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

const styles = {
  page:          { display: "flex", flexDirection: "column", minHeight: "100vh", fontFamily: "'Inter', system-ui, sans-serif" },
  topStripe:     { height: 4, background: `linear-gradient(90deg, ${BLUE}, ${ORANGE})`, flexShrink: 0 },
  body:          { display: "flex", flex: 1 },
  main:          { flex: 1, padding: "40px 48px", overflowY: "auto", background: "#f8fafc" },
  header:        { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28 },
  pageTitle:     { color: "#0f172a", fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: "-0.5px" },
  pageSub:       { color: "#64748b", fontSize: 14, margin: "4px 0 0" },
  addBtn:        { background: ORANGE, border: "none", borderRadius: 10, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600, padding: "10px 20px", whiteSpace: "nowrap", marginTop: 4 },
  successBanner: { background: "rgba(5,150,105,0.08)", border: "1px solid rgba(5,150,105,0.3)", borderRadius: 8, color: "#059669", fontSize: 13, fontWeight: 500, padding: "10px 16px", marginBottom: 20 },
  card:          { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "24px 24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  cardTitle:     { color: "#0f172a", fontSize: 16, fontWeight: 700, margin: "0 0 4px" },
  cardSub:       { color: "#94a3b8", fontSize: 13, margin: "0 0 16px" },
  avatar:        { width: 32, height: 32, borderRadius: "50%", background: ORANGE, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 13, flexShrink: 0 },
  table:         { width: "100%", borderCollapse: "collapse", marginTop: 16 },
  th:            { color: "#94a3b8", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", padding: "10px 12px", textAlign: "left", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" },
  tr:            { borderBottom: "1px solid #f1f5f9" },
  td:            { color: "#374151", fontSize: 14, padding: "12px 12px", verticalAlign: "middle" },
  removeBtn:     { background: "transparent", border: "1px solid #fca5a5", borderRadius: 6, color: "#ef4444", cursor: "pointer", fontSize: 12, padding: "4px 10px" },
  spinner:       { width: 28, height: 28, border: `3px solid #e2e8f0`, borderTop: `3px solid ${BLUE}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" },
};

const modalStyles = {
  overlay:   { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "16px" },
  dialog:    { background: "#fff", borderRadius: 16, padding: "32px", width: "100%", maxWidth: 440, boxShadow: "0 20px 60px rgba(0,0,0,0.15)" },
  header:    { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 },
  title:     { color: "#0f172a", fontSize: 18, fontWeight: 700, margin: 0 },
  closeBtn:  { background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 16, padding: "4px 6px", lineHeight: 1 },
  sub:       { color: "#64748b", fontSize: 13, margin: "0 0 24px" },
  field:     { marginBottom: 16 },
  label:     { display: "block", color: "#374151", fontSize: 13, fontWeight: 500, marginBottom: 6 },
  input:     { width: "100%", padding: "10px 13px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 14, color: "#0f172a", outline: "none", boxSizing: "border-box" },
  select:    { width: "100%", padding: "10px 13px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 14, color: "#0f172a", background: "#fff", cursor: "pointer", boxSizing: "border-box" },
  actions:   { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 },
  cancelBtn: { background: "transparent", border: "1px solid #e2e8f0", borderRadius: 8, color: "#64748b", cursor: "pointer", fontSize: 14, fontWeight: 500, padding: "10px 18px" },
  sendBtn:   { background: ORANGE, border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600, padding: "10px 20px" },
};
